import { Box, Checkbox, FormControlLabel } from "@mui/material";
import type { FC } from "react";
import { useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { AgencySource } from "../../utils/models";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown/SystemDropdown";

export const defaultAgencyOptions = ["Nursa", "Clipboard", "ShiftKey"];
const POSTABLE_AGENCIES = new Set(Object.values(AgencySource).map((s) => s.toLowerCase()));

type PostToAgencyToggleProps = {
	checked: boolean;
	onChange: (checked: boolean) => void;
	selectedAgency?: string | null;
	onAgencyChange?: (agency: string | null) => void;
	agencies?: string[];
};

export const PostToAgencyToggle: FC<PostToAgencyToggleProps> = ({ checked, onChange, selectedAgency, onAgencyChange, agencies }) => {
	// isRealDemo covers inhouse2__ demos, which isAnyDemo misses.
	const { isAnyDemo, isRealDemo } = useHospitalData();

	const postableAgencies = useMemo(
		() => (isAnyDemo || isRealDemo ? defaultAgencyOptions : (agencies ?? []).filter((a) => POSTABLE_AGENCIES.has(a.toLowerCase()))),
		[agencies, isAnyDemo, isRealDemo],
	);

	const hasMultiple = postableAgencies.length > 1;
	const label = hasMultiple ? "Post to Agency" : `Post to ${postableAgencies[0] ?? "Agency"}`;
	const effectiveSelected = selectedAgency ?? postableAgencies[0] ?? null;

	return (
		<Box data-testid="post-shift-agency-toggle" sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
			<FormControlLabel
				sx={{ m: 0 }}
				control={
					<Checkbox
						data-testid="post-to-agency-checkbox"
						checked={checked}
						onChange={(e) => onChange(e.target.checked)}
						sx={(theme) => ({
							color: theme.palette.primary.main,
							"&.Mui-checked": { color: theme.palette.primary.main },
						})}
					/>
				}
				label={label}
			/>

			{hasMultiple && checked && (
				<SystemDropdown<string>
					data-testid="post-to-agency-dropdown"
					label="Agency"
					items={postableAgencies.map((opt) => ({ value: opt, label: opt, selectedLabel: opt }))}
					selectedItem={effectiveSelected}
					onChange={(val) => onAgencyChange?.(val ?? null)}
					fullWidth={false}
					formControlSx={{ minWidth: 180 }}
				/>
			)}
		</Box>
	);
};
